import type { SelectOption } from '@/components/ui/filters/FilterDialog/inputs/types'
import type { FieldSchema, Filter } from '@/stores'
import { numberToCurrency, toCalendarDate } from '@/utils'
import { ACTIVITY_TYPE_LABELS } from '../model/activityView'
import type { ActivityGroup, ActivityQuery, ActivityType } from '../model/types'

// Only the operators the Activity query can answer exactly, so the counts and the rows agree.
const IS = [{ value: 'in', label: 'Is' }]
const CONTAINS = [{ value: 'contains', label: 'Contains' }]
const DATE_OPERATORS = [
  { value: 'between', label: 'Between' },
  { value: 'after', label: 'On or after' },
  { value: 'before', label: 'On or before' },
  { value: 'equals', label: 'On' },
]
const AMOUNT_OPERATORS = [
  { value: 'between', label: 'Between' },
  { value: 'greater_than_or_equal', label: 'At least' },
  { value: 'less_than_or_equal', label: 'At most' },
  { value: 'equals', label: 'Equals' },
]

const typeLabel = (type: string) =>
  ACTIVITY_TYPE_LABELS[type as ActivityType] ?? type

export const activityFilterSchema = (
  accountNames: Map<string, string> | null,
): FieldSchema[] => [
  ...(accountNames
    ? [
        {
          field: 'accountId',
          label: 'Account',
          type: 'select' as const,
          multiple: true,
          operators: IS,
          formatter: (id: string) => accountNames.get(id) ?? id,
        },
      ]
    : []),
  { field: 'date', label: 'Date', type: 'date', operators: DATE_OPERATORS },
  {
    field: 'type',
    label: 'Type',
    type: 'select',
    multiple: true,
    operators: IS,
    formatter: typeLabel,
  },
  { field: 'symbol', label: 'Symbol', type: 'text', operators: CONTAINS },
  {
    field: 'amount',
    label: 'Amount',
    type: 'number',
    operators: AMOUNT_OPERATORS,
    formatter: (value: number) => numberToCurrency(value),
  },
  {
    field: 'description',
    label: 'Description',
    type: 'text',
    operators: CONTAINS,
  },
]

export const activityFilterOptions =
  (
    accountNames: Map<string, string> | null,
    typeCounts: Partial<Record<ActivityType, number>>,
  ) =>
  (field: string): SelectOption[] => {
    if (field === 'accountId' && accountNames) {
      return Array.from(accountNames, ([value, label]) => ({ value, label }))
    }
    if (field === 'type') {
      return (Object.keys(typeCounts) as ActivityType[]).map((type) => ({
        value: type,
        label: ACTIVITY_TYPE_LABELS[type],
      }))
    }
    return []
  }

type DateRange = NonNullable<ActivityQuery['dateRange']>
type AmountRange = NonNullable<ActivityQuery['amount']>

const later = (first?: string, second?: string) =>
  first && second ? (first > second ? first : second) : (first ?? second)

const earlier = (first?: string, second?: string) =>
  first && second ? (first < second ? first : second) : (first ?? second)

const asDate = (value: unknown) =>
  value ? toCalendarDate(value as string | Date) : undefined

const narrowDates = (range: DateRange, filter: Filter): DateRange => {
  const value = asDate(filter.value)
  if (filter.operator === 'after')
    return { ...range, from: later(range.from, value) }
  if (filter.operator === 'before')
    return { ...range, to: earlier(range.to, value) }
  const to = filter.operator === 'between' ? asDate(filter.value2) : value
  return { from: later(range.from, value), to: earlier(range.to, to) }
}

const asNumber = (value: unknown) => {
  const parsed = Number(value)
  return value === '' || value == null || Number.isNaN(parsed)
    ? undefined
    : parsed
}

const narrowAmounts = (range: AmountRange, filter: Filter): AmountRange => {
  const value = asNumber(filter.value)
  if (filter.operator === 'greater_than_or_equal')
    return { ...range, min: value }
  if (filter.operator === 'less_than_or_equal') return { ...range, max: value }
  if (filter.operator === 'between')
    return { min: value, max: asNumber(filter.value2) }
  return { min: value, max: value }
}

const valuesOf = (filter: Filter): string[] =>
  (Array.isArray(filter.value) ? filter.value : [filter.value]).map(String)

const intersect = <Value>(current: Value[] | undefined, next: Value[]) =>
  current ? current.filter((value) => next.includes(value)) : next

export interface TypeChoice {
  group: ActivityGroup | 'all'
  types: ActivityType[]
}

// The house filter chips, the search box and the type group buttons, read as one Activity query.
export const activityQueryOf = (
  base: ActivityQuery,
  filters: Filter[],
  searchQuery: string,
  choice: TypeChoice,
): ActivityQuery => {
  let accountIds = base.accountIds ?? undefined
  let dateRange: DateRange = { ...base.dateRange }
  let amount: AmountRange | undefined
  let types: ActivityType[] | undefined =
    choice.types.length > 0 ? choice.types : undefined
  let symbol: string | undefined
  let description = searchQuery.trim() || undefined
  for (const filter of filters) {
    if (filter.field === 'accountId')
      accountIds = intersect(accountIds, valuesOf(filter))
    if (filter.field === 'date') dateRange = narrowDates(dateRange, filter)
    if (filter.field === 'amount') amount = narrowAmounts(amount ?? {}, filter)
    if (filter.field === 'type')
      types = intersect(types, valuesOf(filter) as ActivityType[])
    if (filter.field === 'symbol') symbol = String(filter.value ?? '')
    if (filter.field === 'description') description = String(filter.value ?? '')
  }
  return {
    accountIds: accountIds ?? null,
    dateRange,
    group: choice.group,
    ...(types ? { types } : {}),
    ...(amount ? { amount } : {}),
    ...(symbol ? { symbol } : {}),
    ...(description ? { description } : {}),
  }
}
